import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useAppContext, JOB_TYPES } from '../context/AppContext';
import supabase from '../supabase';
import JobCard from '../components/jobs/JobCard';
import JobDrawer from '../components/jobs/JobDrawer';
import SafeIcon from '../common/SafeIcon';
import { FiSearch, FiFilter, FiPlus, FiChevronRight, FiMail, FiLoader, FiCheck, FiAlertCircle } from 'react-icons/fi';
import { motion, AnimatePresence } from 'framer-motion';

const Home = () => {
  const { jobs, loading } = useAppContext();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [selectedJob, setSelectedJob] = useState(null);
  const [newsletterEmail, setNewsletterEmail] = useState('');
  const [isSubscribing, setIsSubscribing] = useState(false);
  const [subscribed, setSubscribed] = useState(false);
  const [subscribeError, setSubscribeError] = useState(null);

  const filteredJobs = useMemo(() => {
    if (!jobs) return [];
    
    const baseFiltered = jobs.filter(job => {
      const isExpired = job.expires_at && new Date(job.expires_at) < new Date();
      const isVisible = (job.status === 'active' || job.status === 'published') && !isExpired;
      
      if (!isVisible) return false;
      
      const matchesSearch =
        job.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        job.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
        job.profile?.org_name?.toLowerCase().includes(searchTerm.toLowerCase());
      
      return matchesSearch;
    });

    if (selectedCategory === 'All') {
      return [...baseFiltered].sort((a, b) => {
        const aFeatured = a.is_pinned || a.is_featured;
        const bFeatured = b.is_pinned || b.is_featured;
        if (aFeatured && !bFeatured) return -1;
        if (!aFeatured && bFeatured) return 1;
        return new Date(b.created_at || 0) - new Date(a.created_at || 0);
      });
    } else {
      const siteWideFeatured = baseFiltered
        .filter(j => j.is_pinned || j.is_featured)
        .sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0));

      const categoryRegular = baseFiltered
        .filter(j => 
          !(j.is_pinned || j.is_featured) && 
          j.job_type === selectedCategory
        )
        .sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0));

      return [...siteWideFeatured, ...categoryRegular];
    }
  }, [jobs, searchTerm, selectedCategory]);

  const handleNewsletterSubmit = async (e) => {
    e.preventDefault();
    if (!newsletterEmail) return;
    setIsSubscribing(true);
    setSubscribeError(null);

    try {
      const { error: dbError } = await supabase
        .from('newsletter_subscribers_20240320')
        .insert([{ email: newsletterEmail }]);

      if (dbError && dbError.code !== '23505') {
        throw dbError;
      }

      const { error: edgeError } = await supabase.functions.invoke('sendfox_subscribe', {
        body: { email: newsletterEmail }
      });

      if (edgeError) {
        console.error('SendFox Edge Function error:', edgeError);
      }
      
      setSubscribed(true);
      setNewsletterEmail('');
      setTimeout(() => setSubscribed(false), 5000);
    } catch (err) {
      console.error('Newsletter error:', err);
      setSubscribeError(err.message || 'Failed to join. Please try again.');
    } finally {
      setIsSubscribing(false);
    }
  };

  if (loading) return (
    <div className="flex items-center justify-center min-h-[60vh] bg-stone-50">
      <div className="text-center">
        <div className="w-16 h-16 border-4 border-emerald-500 border-t-transparent rounded-2xl animate-spin mx-auto mb-6"></div>
        <p className="text-stone-500 font-black uppercase tracking-widest text-xs animate-pulse">Finding Jobs...</p>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-stone-50">
      {/* Hero */}
      <div className="relative h-[650px] overflow-hidden">
        <img
          src="https://images.unsplash.com/photo-1464822759023-fed622ff2c3b?auto=format&fit=crop&q=80&w=2000"
          alt="Mountain landscape representing adventure and outdoor careers"
          className="w-full h-full object-cover object-center scale-105"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-stone-900/60 via-stone-900/20 to-transparent" />
        <div className="absolute inset-0 flex flex-col items-center justify-center px-4 text-center">
          <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
            className="text-5xl md:text-8xl font-black text-white mb-8 drop-shadow-2xl tracking-tighter">
            Find Your Next <span className="text-white underline decoration-8 underline-offset-[12px] decoration-emerald-500/80">Adventure Job</span>
          </motion.h1>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
            className="w-full max-w-4xl bg-white/10 backdrop-blur-xl p-3 rounded-[32px] shadow-2xl flex flex-col md:flex-row gap-3 mt-4 border border-white/20">
            <div className="flex-1 flex items-center px-6 py-4 bg-white rounded-[24px] border border-white shadow-inner">
              <SafeIcon icon={FiSearch} className="text-stone-400 mr-4 text-xl" />
              <input type="text" placeholder="Search job titles, keywords, or employers..."
                className="w-full bg-transparent border-none focus:ring-0 text-stone-900 font-bold placeholder:text-stone-400 placeholder:font-medium text-lg"
                value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
            </div>
            <button className="bg-emerald-500 hover:bg-emerald-600 text-white font-black px-12 py-5 rounded-[24px] transition-all shadow-xl uppercase tracking-widest text-sm">
              Search Jobs
            </button>
          </motion.div>
        </div>
      </div>

      <div className="max-w-[1400px] mx-auto px-4 md:px-8 py-16">
        <div className="flex flex-col lg:flex-row gap-12">
          {/* Sidebar */}
          <aside className="lg:w-80 shrink-0 space-y-6">
            <div className="sticky top-24 space-y-6">
              <div className="bg-white rounded-3xl border border-stone-200 p-8 shadow-sm">
                <div className="flex items-center justify-between mb-8 pb-4 border-b border-stone-50">
                  <h3 className="text-xs font-black text-stone-900 uppercase tracking-[0.2em] flex items-center gap-2">
                    <SafeIcon icon={FiFilter} className="text-emerald-500 text-lg" /> Categories
                  </h3>
                  <span className="text-[10px] font-black bg-stone-100 text-stone-500 px-3 py-1 rounded-full uppercase tracking-wider">{filteredJobs.length}</span>
                </div>
                <div className="space-y-2 overflow-y-auto max-h-[600px] pr-2 custom-scrollbar">
                  {['All', ...JOB_TYPES].map((cat) => (
                    <button key={cat} onClick={() => setSelectedCategory(cat)}
                      className={`w-full flex items-center justify-between px-5 py-4 rounded-2xl text-xs font-black uppercase tracking-widest transition-all text-left ${
                        selectedCategory === cat ? 'bg-emerald-600 text-white shadow-lg shadow-emerald-600/20' : 'text-stone-500 hover:bg-stone-50'
                      }`}>
                      <span>{cat === 'All' ? 'All Jobs' : cat}</span>
                      {selectedCategory === cat && <SafeIcon icon={FiChevronRight} />}
                    </button>
                  ))}
                </div>
              </div>

              {/* Newsletter */}
              <div className="bg-emerald-900 rounded-[32px] p-8 text-white shadow-2xl relative overflow-hidden group">
                <div className="absolute -right-4 -top-4 w-24 h-24 bg-emerald-500/20 rounded-full blur-2xl group-hover:scale-150 transition-transform duration-700" />
                <h4 className="text-xs font-black uppercase tracking-[0.3em] mb-4 flex items-center gap-2">
                  <SafeIcon icon={FiMail} className="text-emerald-400" /> Newsletter
                </h4>
                <p className="text-emerald-100/70 text-xs font-bold leading-relaxed uppercase tracking-widest mb-6">
                  Get monthly updates and top featured jobs delivered to your inbox.
                </p>
                <form onSubmit={handleNewsletterSubmit} className="space-y-3">
                  <input type="email" required placeholder="Email Address..."
                    className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-3 text-sm font-bold placeholder:text-white/30 outline-none focus:ring-2 focus:ring-emerald-400"
                    value={newsletterEmail} onChange={(e) => setNewsletterEmail(e.target.value)} />
                  
                  <AnimatePresence mode="wait">
                    {subscribeError && (
                      <motion.p 
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        className="text-[10px] text-red-400 font-bold uppercase tracking-wider flex items-center gap-1"
                      >
                        <SafeIcon icon={FiAlertCircle} /> {subscribeError}
                      </motion.p>
                    )}
                  </AnimatePresence>

                  <button disabled={isSubscribing || subscribed}
                    className={`w-full py-3 rounded-xl font-black uppercase tracking-widest text-[10px] transition-all flex items-center justify-center gap-2 ${
                      subscribed ? 'bg-white text-emerald-600' : 'bg-emerald-500 hover:bg-emerald-400 text-white'
                    }`}>
                    {isSubscribing ? <SafeIcon icon={FiLoader} className="animate-spin" /> : subscribed ? <><SafeIcon icon={FiCheck} /> Subscribed</> : 'Join List'}
                  </button>
                </form>
                <div className="mt-6 pt-6 border-t border-white/10 text-center">
                  <Link to="/newsletters" className="text-emerald-400/80 hover:text-emerald-300 text-[9px] font-black uppercase tracking-[0.2em] transition-colors">
                    Read past issues
                  </Link>
                </div>
              </div>

              <div className="bg-stone-900 rounded-[24px] p-6 text-white relative overflow-hidden group shadow-2xl">
                <div className="absolute -right-6 -bottom-6 w-32 h-32 bg-emerald-500/20 rounded-full blur-2xl group-hover:scale-150 transition-transform duration-700" />
                <h4 className="font-black text-sm mb-3 relative z-10 uppercase tracking-widest">Hiring?</h4>
                <p className="text-xs text-stone-400 mb-6 relative z-10 leading-relaxed font-bold uppercase tracking-widest">
                  Post your job and reach thousands of candidates.
                </p>
                <Link to="/employer/post-job"
                  className="flex items-center justify-center gap-2 w-full py-4 bg-emerald-500 text-white rounded-xl text-[10px] font-black uppercase tracking-[0.2em] hover:bg-emerald-400 transition-all relative z-10">
                  <SafeIcon icon={FiPlus} /> Post a Job
                </Link>
              </div>
            </div>
          </aside>

          {/* Job Grid */}
          <main className="flex-1">
            <div className="flex items-center justify-between mb-10">
              <div>
                <h2 className="text-3xl font-black text-stone-900 tracking-tighter uppercase">
                  {selectedCategory === 'All' ? 'Latest Jobs' : `${selectedCategory}`}
                </h2>
                <p className="text-stone-500 font-bold uppercase tracking-widest text-[10px] mt-2">
                  Showing {filteredJobs.length} active jobs
                </p>
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {filteredJobs.length > 0 ? filteredJobs.map((job) => (
                <JobCard key={job.id} job={job} onClick={() => setSelectedJob(job)} />
              )) : (
                <div className="col-span-full py-32 text-center bg-white rounded-[40px] border-2 border-dashed border-stone-200">
                  <div className="w-20 h-20 bg-stone-50 rounded-3xl flex items-center justify-center mx-auto mb-6 border border-stone-100">
                    <SafeIcon icon={FiSearch} className="text-3xl text-stone-300" />
                  </div>
                  <h3 className="text-xl font-black text-stone-900 uppercase tracking-widest">No Jobs Found</h3>
                  <p className="text-stone-400 mt-3 font-bold uppercase tracking-widest text-xs">Try adjusting your filters or search terms.</p>
                </div>
              )}
            </div>
          </main>
        </div>
      </div>
      
      <JobDrawer job={selectedJob} isOpen={!!selectedJob} onClose={() => setSelectedJob(null)} />
    </div>
  );
};

export default Home;